/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * State management hook for saved views
 */

"use client";

import { logger } from "@/lib/utils/logger";
import { useEffect, useRef, useState } from "react";

interface UseSavedViewsStateOptions {
  viewType: string;
  organizationId: string | null;
  syncWithUrl: boolean;
}

/**
 * Hook for managing saved views state (selectedViewId, localStorage, refs)
 */
export function useSavedViewsState<TFilters>({
  viewType,
  organizationId,
  syncWithUrl,
}: UseSavedViewsStateOptions) {
  // Store viewId in localStorage + component state (not URL)
  // URL state causes render loops - viewId is user preference, not shareable state
  // Filters remain in URL (they're shareable)
  const localStorageKey = `saved-view-${viewType}-${organizationId}`;

  // Initialize from localStorage
  const [selectedViewId, setSelectedViewId] = useState<string | undefined>(
    () => {
      if (typeof window === "undefined" || !syncWithUrl) return undefined;
      try {
        const stored = localStorage.getItem(localStorageKey);
        return stored ? JSON.parse(stored) : undefined;
      } catch {
        return undefined;
      }
    },
  );

  // Persist to localStorage when viewId changes
  useEffect(() => {
    if (typeof window === "undefined" || !syncWithUrl) return;
    try {
      if (selectedViewId) {
        localStorage.setItem(localStorageKey, JSON.stringify(selectedViewId));
      } else {
        localStorage.removeItem(localStorageKey);
      }
    } catch (error) {
      logger.warn("Failed to persist viewId to localStorage", { error });
    }
  }, [selectedViewId, localStorageKey, syncWithUrl]);

  // Use selectedViewId directly (no longer from URL)
  const selectedViewIdValue = selectedViewId ?? "";

  // Track if we're currently applying a view to prevent loops
  const applyingViewRef = useRef(false);
  // Store currentFilters in a ref to avoid triggering useEffect on every filter change
  const currentFiltersRef = useRef<TFilters | undefined>(undefined);
  // Track the last applied viewId to prevent re-application loops
  const lastAppliedViewIdRef = useRef<string | null>(null);

  return {
    selectedViewId,
    setSelectedViewId,
    selectedViewIdValue,
    applyingViewRef,
    currentFiltersRef,
    lastAppliedViewIdRef,
  };
}
